import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, CardHeader, Col, Form, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { FileUploader } from "react-drag-drop-files";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Swal from "sweetalert2";
import { useApiStore } from "../../hooks/ApiStoreContext";

const fileTypes = ["JPEG", "JPG", "PNG"];

const AddBanner = () => {
  const apiStore = useApiStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [heading, setHeading] = useState("Add");
  const [file, setFile] = useState(null);
  const [fileChange, setFileChange] = useState(false);

  const handleFileChange = (file) => {
    setFile(file);
    setFileChange(true);
  };

  const handleFileDownload = () => {
    if (file) {
      const url = URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    defaultValues: {
      bannerId: 0,
      name: "",
      description: "",
      link: "",
      imageSource: "",
      displaySequence: 0,
      isActive: true,
      createdDate: new Date().toISOString(),
    },
  });

  const handleBack = (e) => {
    e.preventDefault();
    navigate("/banner");
  };

  const fetchBannerData = async () => {
    let bannerId = location.pathname.split("/")[2];
    if (bannerId) {
      setHeading("Edit");

      try {
        const banner = await apiStore.get(`/banners/${bannerId}`);
        setValue("bannerId", banner.bannerId);
        setValue("name", banner.name);
        setValue("description", banner.description);
        setValue("link", banner.link);
        setValue("imageSource", banner.imageSource);
        setValue("displaySequence", banner.displaySequence);
        setValue("isActive", banner.isActive);
        setValue("createdDate", banner.createdDate);

        const imageBlob = await apiStore.getBlob(`/banners/getImage/${bannerId}`);
        const filename = banner.imageSource.split("/").pop();
        const type = imageBlob.type;

        const fetchedImageFile = new File([imageBlob], filename, { type });
        setFile(fetchedImageFile);
      } catch (error) {
        if (error.response.status !== 401) {
          console.error("Error fetching banner data:", error);
        }
      }
    }
  };

  useEffect(() => {
    fetchBannerData();
  }, [location, setValue]);

  const onSubmit = async (data) => {
    if (!file) {
      await Swal.fire({ icon: "warning", title: "Oops...", text: "Please upload an Image before submitting." });
      return;
    }

    try {
      if (fileChange) {
        const formData = new FormData();
        formData.append("image", file);

        const addImageResponse = await apiStore.postFormData("/banners/addImage", formData);
        if (!!data.imageSource && !!data.bannerId) {
          try {
            await apiStore.delete(`/banners/deleteImage/${data.bannerId}`);
          } catch (error) {
            console.error("There was an error deleting the existing banner image!", error);
          }
        }

        data.imageSource = addImageResponse.imageSource;
      }

      if (data.bannerId === 0) {
        await apiStore.post("/banners", data);
        await Swal.fire({ title: "Success", text: "Banner saved successfully!", icon: "success", confirmButtonText: "OK" });
      } else {
        await apiStore.put(`/banners/${data.bannerId}`, data);
        await Swal.fire({ title: "Updated", text: "Banner updated successfully!", icon: "success", confirmButtonText: "OK" });
      }

      navigate("/banner");
    } catch (error) {
      if (error.response.status !== 401) {
        console.error("Error saving banner:", error);
        await Swal.fire({ icon: "error", title: "Error", text: "There was an error while saving the banner" });
      }
    }
  };

  const handleCancel = (e) => {
    console.log("cancel clicked");
    reset({
      bannerId: 0,
      name: "",
      description: "",
      link: "",
      imageSource: "",
      displaySequence: 1,
      isActive: true,
      createdDate: new Date().toISOString(),
    });
    setFile(null);
    navigate("/banner");
  };

  return (
    <Row>
      <Col md={12}>
        <Card className="mb-4">
          <CardHeader className="pb-0">
            <Row>
              <Col md={9}>
                <h4>{heading} Banner Details</h4>
              </Col>
              <Col md={3} className="d-grid d-md-flex justify-content-md-end d-sm-block">
                <Button variant="outline-primary" className="btn-sm" onClick={handleBack}>
                  Back
                </Button>
              </Col>
            </Row>
          </CardHeader>
          <CardBody className="px-0 pt-0 pb-2">
            <Form onSubmit={handleSubmit(onSubmit)} className="p-3">
              <Row className="mb-3">
                <input type="hidden" {...register("bannerId")} />
                <Form.Group controlId="name" className="col-md-6 col-sm-12">
                  <Form.Label>
                    Banner Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control type="text" placeholder="Banner Name" {...register("name", { required: "Banner Name is required." })} />
                  <span className="text-xs text-danger">{errors.name?.message}</span>
                </Form.Group>
                <Form.Group controlId="link" className="col-md-6 col-sm-12">
                  <Form.Label>
                    Banner Link <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control type="text" placeholder="Banner Link" {...register("link", { required: "Banner Link is required." })} />
                  <span className="text-xs text-danger">{errors.link?.message}</span>
                </Form.Group>
              </Row>
              <Row className="mb-3">
                <Form.Group controlId="description" className="col-md-6 col-sm-12">
                  <Form.Label>
                    Description <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control as="textarea" rows={3} placeholder="Description" {...register("description", { maxLength: { value: 500, message: "Maximum 500 characters allowed." } })} />
                  <span className="text-xs text-danger">{errors.description?.message}</span>
                </Form.Group>
                <Form.Group controlId="imageSource" className="col-md-6 col-sm-12">
                  <Form.Label>
                    Image <span className="text-danger">*</span>
                  </Form.Label>
                  <FileUploader multiple={false} handleChange={handleFileChange} name="file" types={fileTypes} fileOrFiles={file ? [file] : null} />
                  <p>
                    {file ? (
                      <>
                        {file.name} <FontAwesomeIcon icon={faDownload} onClick={handleFileDownload} style={{ cursor: "pointer" }} />
                      </>
                    ) : (
                      "No file uploaded yet"
                    )}
                  </p>
                </Form.Group>
              </Row>
              <Row className="mb-3">
                <Form.Group controlId="displaySequence" className="col-md-6 col-sm-12">
                  <Form.Label>
                    Display Sequence <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control type="number" placeholder="Display Sequence" {...register("displaySequence", { required: "Display Sequence is required." })} />
                  <span className="text-xs text-danger">{errors.displaySequence?.message}</span>
                </Form.Group>
                <Form.Group controlId="isActive" className="col-md-6 col-sm-12">
                  <Form.Label>Is Active</Form.Label>
                  <Form.Check type="switch" {...register("isActive")} />
                </Form.Group>
              </Row>
              <Row>
                <Col md={12} className="d-grid gap-2 d-md-flex flex-md-row-reverse">
                  <Button variant="primary" className="me-md-2" type="submit">
                    Submit
                  </Button>
                  <Button variant="dark" type="button" onClick={handleCancel}>
                    Cancel
                  </Button>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default AddBanner;
